// props to kent c dodds epic react

import type { Dispatch, SetStateAction } from "react"
import { useEffect, useRef, useState } from "react"

interface IUseSessionStorage<S> {
  key: string
  initialState: S | (() => S)
  serialize?: (data: S) => string
  deserialize?: (serialized: string) => S
}

const useSessionStorageeState = <S>({
  key,
  initialState,
  serialize = JSON.stringify,
  deserialize = JSON.parse,
}: IUseSessionStorage<S>): [S, Dispatch<SetStateAction<S>>, VoidFunction] => {
  const [state, setState] = useState(() => {
    const valueInSessionStorage = window.sessionStorage.getItem(key)
    if (valueInSessionStorage) return deserialize(valueInSessionStorage)

    return typeof initialState === `function`
      ? (initialState as CallableFunction)()
      : initialState
  })

  const prevKeyRef = useRef(key)
  const clearState = () => {
    window.sessionStorage.removeItem(prevKeyRef.current)
    setState(``)
  }

  useEffect(() => {
    const prevKey = prevKeyRef.current
    if (prevKey !== key) {
      window.sessionStorage.removeItem(prevKey)
    }
    prevKeyRef.current = key
    window.sessionStorage.setItem(key, serialize(state))
  }, [key, serialize, state])

  return [state, setState, clearState]
}

export default useSessionStorageeState
